import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useMultiSelect, multiSelect } from '@szhsin/react-autocomplete';
import { pick } from 'lodash-es';

import LoadingSpinner from '../../../../LoadingSpinner';
import useDebounce from '../../../../../hooks/useDebounce';
import axios, { isCancelRequest, buildRequestCancelation } from '../../../../../configs/axios';

const LIMIT = 20;
const defaultParams = {
  term: '',
  limit: LIMIT,
  offset: 0,
  hasMore: false,
  context: 'custom_export_recipients',
  include_self: 'true',
};
const infiniteScrollStyle = { overflow: 'visible' };

const { getCancelToken } = buildRequestCancelation();

const isEqualById = (a, b) => a?.id === b?.id;
const getItemValue = (user) => user.value;
const getUserName = (user) => user.label?.split('(')[0]?.trim() || '';

const eliminateEmailValue = (value) => value.trim().replace(/\s+/g, '-').toLowerCase();
const decorateCreatableEmail = (value, preselectedDomain = '') => {
  const eliminatedValue = eliminateEmailValue(value);
  if (eliminatedValue.includes('@')) return eliminatedValue;
  return `${eliminatedValue}@${preselectedDomain}`;
};
const validateDomain = (email, allowedDomains) => {
  const domain = email.split('@')[1];
  return {
    present: !!domain,
    valid: allowedDomains.includes(domain),
  };
};

const multiSelectFeature = multiSelect({ closeOnSelect: false });

const RecipientsSelect = ({ selected, allowedDomains, onChange }) => {
  const scrollId = useId();

  const [items, setItems] = useState([]);
  const [isPending, setPending] = useState(false);

  const [value, setValue] = useState('');
  const debouncedTerm = useDebounce(value, 500);

  const paramsRef = useRef(defaultParams);

  const selectedEmails = useMemo(() => new Set(selected.map((s) => s.value)), [selected]);
  const availableItems = useMemo(
    () => items.filter((user) => !selectedEmails.has(user.value)),
    [items, selectedEmails]
  );
  const withCreatableItems = useMemo(() => {
    const eliminatedValue = eliminateEmailValue(value);
    if (eliminatedValue) {
      const candidateEmail = decorateCreatableEmail(value, allowedDomains[0]);
      if (
        !selected.some((item) => item.value === candidateEmail) &&
        !items.some((item) => !item.creatable && item.value.startsWith(eliminatedValue))
      ) {
        return [{ value: eliminatedValue, creatable: true }, ...availableItems];
      }
    }

    return availableItems;
  }, [value, selected, items, availableItems, allowedDomains]);

  const fetchUsers = useCallback(() => {
    setPending(true);

    axios
      .get('/users', {
        params: pick(paramsRef.current, ['term', 'limit', 'offset', 'context', 'include_self']),
        cancelToken: getCancelToken('recipients').token,
      })
      .then(({ data }) => {
        paramsRef.current = {
          ...paramsRef.current,
          hasMore: data.length === LIMIT,
        };
        // If offset is 0, we replace the items, otherwise we append to the list
        setItems((prev) => (paramsRef.current.offset === 0 ? data : [...prev, ...data]));
        setPending(false);
      })
      .catch((error) => {
        if (!isCancelRequest(error)) {
          setPending(false);
        }
      });
  }, []);

  const loadMore = useCallback(() => {
    if (isPending || !paramsRef.current.hasMore) return;

    paramsRef.current = {
      ...paramsRef.current,
      hasMore: false,
      offset: paramsRef.current.offset + LIMIT,
    };

    fetchUsers();
  }, [isPending, fetchUsers]);

  useEffect(() => {
    setItems([]);

    paramsRef.current = {
      ...defaultParams,
      term: debouncedTerm,
    };

    fetchUsers();
  }, [debouncedTerm, fetchUsers]);

  useEffect(() => {
    // If all available items are selected and there are more items to load, fetch more users
    if (availableItems.length === 0 && paramsRef.current.hasMore) {
      loadMore();
    }
  }, [availableItems]);

  const {
    getInputProps,
    getListProps,
    getItemProps,
    getFocusCaptureProps,
    open,
    setOpen,
    removeSelect,
  } = useMultiSelect({
    feature: multiSelectFeature,
    items: withCreatableItems,
    value,
    selected,
    onChange: (v) => setValue(v ?? ''),
    onSelectChange: (next) => {
      setValue('');
      onChange(next);
    },
    // Specify how to determine if an item is an action,
    isItemAction: (item) => !!item.creatable,
    // and what happens after the action is triggered.
    onAction: (item) => {
      if (item.creatable) {
        const domainValidation = validateDomain(item.value, allowedDomains);
        if (domainValidation.present && !domainValidation.valid) return;

        const decoratedEmail = decorateCreatableEmail(item.value, allowedDomains[0]);
        onChange([
          ...selected,
          {
            value: decoratedEmail,
            label: decoratedEmail,
            id: decoratedEmail,
          },
        ]);
      }
    },
    isEqual: isEqualById,
    getItemValue,
  });

  return (
    <div className="recipients-select">
      <div
        className="recipients-select__control"
        {...getFocusCaptureProps()}
        onClick={() => setOpen(true)}
      >
        {selected.map((user) => (
          <span key={user.id} className="recipients-select__pill">
            <span>{getUserName(user) || user.value}</span>
            <span
              className="hicon-close-small recipients-select__pill-remove"
              onClick={() => removeSelect(user)}
            />
          </span>
        ))}
        <input
          {...getInputProps()}
          autoComplete="nope"
          className="hms-input-text recipients-select__input"
          placeholder={selected.length ? '' : 'Emails of recipients'}
        />
      </div>
      {open && (
        <div {...getListProps()} id={scrollId} className="recipients-select__menu">
          <InfiniteScroll
            dataLength={availableItems.length}
            hasMore={paramsRef.current.hasMore}
            next={loadMore}
            scrollThreshold={0.95}
            scrollableTarget={scrollId}
            style={infiniteScrollStyle}
          >
            {withCreatableItems.map((user, index) => {
              const name = getUserName(user);
              const domainValidation = user.creatable
                ? validateDomain(user.value, allowedDomains)
                : null;
              return (
                <div
                  key={user.id || index}
                  {...getItemProps({ item: user, index })}
                  className="recipients-select__option"
                >
                  {name && (
                    <div className="recipients-select__option-primary text-truncate">{name}</div>
                  )}
                  <div className="recipients-select__option-secondary">
                    {user.creatable ? (
                      <div className="d-flex flex-column gap-4 align-items-start">
                        <p>
                          Add a new user: &quot;
                          {decorateCreatableEmail(user.value, allowedDomains[0])}&quot;
                        </p>
                        {domainValidation?.present && !domainValidation.valid && (
                          <p className="text-12 text-secondary text-left">
                            <span className="text-rose">Invalid domain.</span> (Allowed:{' '}
                            {allowedDomains.join(', ')})
                          </p>
                        )}
                      </div>
                    ) : (
                      user.value
                    )}
                  </div>
                </div>
              );
            })}
            {isPending && (
              <LoadingSpinner
                size="sm"
                id="recipients-select-more"
                className="text-blue justify-content-center"
              />
            )}
          </InfiniteScroll>
          {!isPending && items.length === 0 && withCreatableItems.length === 0 && (
            <div className="recipients-select__empty">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

RecipientsSelect.propTypes = {
  selected: T.array.isRequired,
  allowedDomains: T.array.isRequired,
  onChange: T.func.isRequired,
};

export default RecipientsSelect;
